// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

/**
 * SupportTeamPage
 *
 * Page for advisors to view their assigned support staff members.
 * Shows support team information, recent activity by support, and contact details.
 */

import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatRelativeTime, formatRelativeTimeShort } from '@loa/theme';
import { Alert, AppIcon, Spinner, Button } from '@loa/ui-components';
import { useAdvisor } from '../contexts/AdvisorContext';
import { supportApi } from '../api/supportApi';
import type { SupportTeamMember, ActivityLogEntry } from '../types/support';

// ============================================
// Support Team Member Card Component
// ============================================

type SupportMemberCardProps = Readonly<{
  member: SupportTeamMember;
  onViewActivity: () => void;
}>;

function SupportMemberCard({ member, onViewActivity }: SupportMemberCardProps) {
  const initials = `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();

  return (
    <div className="fin-card">
      <div className="fin-card__content">
        {/* Header with Avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '1em', marginBottom: '1em' }}>
          {member.photoUrl ? (
            <img
              src={member.photoUrl}
              style={{ width: '64px', height: '64px', borderRadius: '50%' }}
              alt={`${member.firstName} ${member.lastName}`}
            />
          ) : (
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#00B5AD',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.25rem',
                fontWeight: 'bold',
              }}
            >
              {initials}
            </div>
          )}
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '0.25em', fontSize: '1.25em', fontWeight: '600' }}>
              {member.firstName} {member.lastName}
            </div>
            <span style={{ fontSize: '0.75em', background: '#00B5AD', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>
              <AppIcon name="headphones" /> Support
            </span>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{ marginBottom: '1em' }}>
          <div style={{ marginBottom: '0.5em' }}>
            <AppIcon name="mail" color="grey" />
            <a href={`mailto:${member.email}`} style={{ marginLeft: '0.5em' }}>
              {member.email}
            </a>
          </div>
          {member.phoneNumber && (
            <div>
              <AppIcon name="phone" color="grey" />
              <a href={`tel:${member.phoneNumber}`} style={{ marginLeft: '0.5em' }}>
                {member.phoneNumber}
              </a>
            </div>
          )}
        </div>

        {/* Metadata */}
        <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: '1em' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1em' }}>
            <div>
              <div style={{ fontSize: '0.85em', color: '#666' }}>Assigned</div>
              <div style={{ fontWeight: '500' }}>
                {formatDate(member.assignedAt)}
              </div>
              <div style={{ fontSize: '0.85em', color: '#999' }}>
                by {member.assignedByName}
              </div>
            </div>
            <div>
              <div style={{ fontSize: '0.85em', color: '#666' }}>Last Active</div>
              <div style={{ fontWeight: '500' }}>
                {formatRelativeTime(member.lastActiveAt)}
              </div>
            </div>
          </div>
        </div>

        {/* Actions This Week */}
        <div
          style={{
            marginTop: '1em',
            padding: '0.75em',
            backgroundColor: '#f9f9f9',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div>
            <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#21BA45' }}>
              {member.actionsThisWeek}
            </span>
            <span style={{ marginLeft: '0.5em', color: '#666' }}>
              actions this week
            </span>
          </div>
        </div>
      </div>
      <div style={{ padding: '0.75em 1em', borderTop: '1px solid #f0f0f0' }}>
        <Button variant="outlined" fluid onClick={onViewActivity}>
          <AppIcon name="history" />
          View Activity
        </Button>
      </div>
    </div>
  );
}

// ============================================
// Recent Support Activity Component
// ============================================

type RecentSupportActivityProps = Readonly<{
  activities: ActivityLogEntry[];
  loading: boolean;
  onViewAll: () => void;
}>;

function RecentSupportActivity({ activities, loading, onViewAll }: RecentSupportActivityProps) {
  const getActivityIcon = (type: string): string => {
    switch (type) {
      case 'PROFILE_CREATED':
        return 'plus';
      case 'PROFILE_UPDATED':
        return 'edit';
      case 'CLIENT_ADDED':
        return 'user plus';
      case 'CLIENT_UPDATED':
        return 'user';
      case 'POLICY_ADDED':
        return 'file alternate';
      case 'POLICY_UPDATED':
        return 'file';
      case 'LOA_REMINDER':
        return 'bell';
      case 'INVITATION_SENT':
        return 'envelope';
      default:
        return 'circle';
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
          <h4 style={{ margin: 0 }}>
            <AppIcon name="history" />
            Recent Support Activity
          </h4>
        </div>
        <Spinner />
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1em' }}>
        <h4 style={{ margin: 0 }}>
          <AppIcon name="history" />
          Recent Support Activity
        </h4>
        <Button variant="outlined" size="sm" onClick={onViewAll}>
          View All
        </Button>
      </div>

      {activities.length === 0 ? (
        <Alert variant="info">
          <p>No recent activity from support staff.</p>
        </Alert>
      ) : (
        <div>
          {activities.map((activity) => (
            <div
              key={activity.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1em',
                padding: '0.75em 0',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              <div
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#00B5AD',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                <AppIcon name={getActivityIcon(activity.type) as 'plus' | 'edit' | 'user plus' | 'user' | 'file alternate' | 'file' | 'bell' | 'envelope' | 'circle'} style={{ margin: 0 }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '500' }}>{activity.title}</div>
                <div style={{ fontSize: '0.85em', color: '#666' }}>
                  {activity.subtitle}
                </div>
                <div style={{ fontSize: '0.8em', color: '#999', marginTop: '0.25em' }}>
                  <span style={{ background: '#00B5AD', color: 'white', padding: '1px 5px', borderRadius: '3px', fontSize: '0.85em' }}>
                    {activity.actorName}
                  </span>
                  <span style={{ marginLeft: '0.5em' }}>
                    {formatRelativeTimeShort(activity.timestamp)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function SupportTeamPage() {
  const navigate = useNavigate();
  const { advisor, loading: advisorLoading, error: advisorError } = useAdvisor();

  // State
  const [supportTeam, setSupportTeam] = useState<SupportTeamMember[]>([]);
  const [recentActivity, setRecentActivity] = useState<ActivityLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ============================================
  // Data Loading
  // ============================================

  useEffect(() => {
    const loadSupportTeam = async () => {
      if (!advisor) return;

      try {
        setLoading(true);
        setError(null);

        // Use different API based on user role
        const isAdmin = advisor.role === 'ADMIN';
        const teamPromise = isAdmin
          ? supportApi.getAdminSupportStaffList()
          : supportApi.getMySupportTeam();

        const [teamResponse, activityResponse] = await Promise.all([
          teamPromise,
          // Activity log is only available for USER role (advisors)
          isAdmin
            ? Promise.resolve({ content: [] })
            : supportApi.getActivityLog(
                { actorFilter: 'support-actions', actionType: 'all' },
                0,
                10
              ),
        ]);

        setSupportTeam(teamResponse);
        setRecentActivity(activityResponse.content);
      } catch (err) {
        console.error('Failed to load support team:', err);
        setError('Failed to load support team');
      } finally {
        setLoading(false);
      }
    };

    loadSupportTeam();
  }, [advisor]);

  // ============================================
  // Event Handlers
  // ============================================

  const handleViewMemberActivity = useCallback((memberId: string) => {
    navigate(`/activity-log?supportPersonId=${memberId}`);
  }, [navigate]);

  const handleViewAllActivity = useCallback(() => {
    navigate('/activity-log?actorFilter=support-actions');
  }, [navigate]);

  // ============================================
  // Loading and Error States
  // ============================================

  if (advisorLoading) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Spinner />
      </div>
    );
  }

  if (advisorError) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="error" header="Error Loading Account">
          <p>{advisorError}</p>
        </Alert>
      </div>
    );
  }

  if (!advisor) {
    return (
      <div style={{ marginTop: '3em' }}>
        <Alert variant="warning" header="No Account Found">
          <p>Please contact your administrator.</p>
        </Alert>
      </div>
    );
  }

  // ============================================
  // Page Layout
  // ============================================

  return (
    <div style={{ padding: '2em 3em' }}>
      {/* Breadcrumb */}
      <nav style={{ marginBottom: '1em', fontSize: '0.9em', color: '#666' }}>
        <button
          type="button"
          style={{ cursor: 'pointer', color: '#4169E1', background: 'none', border: 0, padding: 0, font: 'inherit' }}
          onClick={() => navigate('/')}
        >
          Dashboard
        </button>
        {' / '}
        <span>Support Team</span>
      </nav>

      {/* Header */}
      <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5em' }}>
        <AppIcon name="users" />
        My Support Team
      </h2>
      <p style={{ color: '#666', marginBottom: '1.5em' }}>
        Support staff assigned to assist you with your client profiles
      </p>

      {/* Info Message */}
      <Alert variant="info" header="What Support Staff Can Do" style={{ marginBottom: '1.5em' }}>
        <ul>
          <li>View your profiles, clients, and policies</li>
          <li>Create new profiles and add clients on your behalf</li>
          <li>Send LOA reminders and client invitations</li>
          <li>Update profile and client information</li>
        </ul>
        <p style={{ marginTop: '0.5em', fontStyle: 'italic', color: '#666' }}>
          Note: Support staff cannot create or void Letters of Authority.
        </p>
      </Alert>

      {/* Error Message */}
      {error && (
        <Alert variant="error" header="Error" style={{ marginBottom: '1.5em' }}>
          <p>{error}</p>
        </Alert>
      )}

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', gap: '1.5em' }}>
          <div style={{ flex: '0 0 60%' }}>
            <Spinner />
          </div>
          <div style={{ flex: '0 0 40%' }}>
            <Spinner />
          </div>
        </div>
      ) : supportTeam.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '3em', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
          <AppIcon name="users" color="grey" />
          <h3>No Support Staff Assigned</h3>
          <p style={{ color: '#666' }}>
            Contact your brokerage administrator to request support staff assistance.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '1.5em', flexWrap: 'wrap' }}>
          {/* Left Column: Support Team Cards */}
          <div style={{ flex: '0 0 60%', minWidth: '300px' }}>
            <div style={{ padding: '1rem', background: 'white', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
              <h3 style={{ marginBottom: '1em' }}>
                <AppIcon name="users" />
                Assigned Support Staff ({supportTeam.length})
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1em' }}>
                {supportTeam.map((member) => (
                  <SupportMemberCard
                    key={member.id}
                    member={member}
                    onViewActivity={() => handleViewMemberActivity(member.id)}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column: Recent Activity */}
          <div style={{ flex: '1 1 300px' }}>
            <RecentSupportActivity
              activities={recentActivity}
              loading={loading}
              onViewAll={handleViewAllActivity}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default SupportTeamPage;
